import { VForm, GlobalResponseType } from '@/features/common/types';
import { UploadDocument } from './documentType';

// TODO: remove this one & re-use the below PackageType
export enum PackageEnum {
  MyPackage = 'My Package',
  Envelope = 'Envelope',
  Pak = 'Pak',
  UserDefined = 'User Defined',
  Pallet = 'Skid / Pallet',
}

export enum PackageType {
  MyPackage = 'MyPackage',
  Envelope = 'Envelope',
  Pak = 'Pak',
  UserDefined = 'UserDefined',
  Pallet = 'Pallet'
}

export enum WeightUnitType { 'Lbs', 'Kgs' }

export enum CurrencyCodeType { 'CAD', 'USD' }

export enum DimensionUnitType { 'Inches', 'CM' }

export enum SignatureType {
  None = 'None',
  SignatureRequired ='SignatureRequired',
  AdultSignatureRequired = 'AdultSignatureRequired',
}

export enum ExportReasonType {
  'Permanent',
  'Temporary',
  'Gift',
  'Repair',
  'Sample',
  'PersonalEffects',
  'Return',
  'CommercialSample',
}

export enum BillingType {
  Prepaid = 'Prepaid',
  FreightCollect = 'FreightCollect',
  ThirdParty = 'ThirdParty',
  FreeDomicile = 'FreeDomicile',
  DDP = 'DDP'
}

export enum PrintLabelSize { 'Standard', 'Thermal' }

export enum CardType {
  Visa = 'Visa',
  MasterCard = 'MasterCard',
  Discover = 'Discover',
  AmericanExpress = 'AmericanExpress',
  DinersClub = 'DinersClub',
  JCB = 'JCB',
  Unknown = 'Unknown'
}

export enum PaymentMethodType {
  Check = 'Check',
  CreditCard = 'CreditCard',
  DirectDeposit = 'DirectDeposit'
}

export enum CarrierCodeType {
  'UPS',
  'FedEx',
  'Purolator',
  'DHL',
  'Canpar',
  'ICS',
  'PTC',
  'Canadapost',
  'Loomis',
  'GLS',
  'Trexity',
  'USPS',
  'TotalShip',
}

export enum TypeOfRateType {
  'PublishedRate',
  'SecureshipRate',
  'MyAccount',
}

export enum ShipStatus {
  Created = 'Created',
  Exception = 'Exception',
  Delivered = 'Delivered',
  InTransit = 'InTransit',
}

export enum AppointmentType {
  None = 'None',
  Required = 'Required',
  Scheduled = 'Scheduled',
}

export interface Appointment {
  appointmentType: keyof typeof AppointmentType,
  phone?: string,
  date: string, // YYYY-MM-DD
  time?: string // 3:00 PM
}

export interface ShipFrom {
  company: string,
  contact: string,
  phone: string,
  addr1: string,
  addr2?:string,
  addr3?: string,
  countryCode: string,
  postalCode: string,
  province?: string,
  city: string,
  residential: boolean,
  taxId?: string,
  emails?: Array<string>,
  isInside: boolean,
  isTailGate: boolean,
  isTradeShow: boolean,
  isLimitedAccess: boolean,
  appointment?: Appointment,
}

export interface ShipTo {
  company: string,
  contact: string,
  phone: string,
  addr1: string,
  addr2?:string,
  addr3?:string,
  countryCode: string,
  postalCode: string,
  province?: string,
  city: string,
  emails?: Array<string>,
  residential: boolean,
  taxId?: string,
  isInside: boolean,
  isTailGate: boolean,
  isTradeShow: boolean,
  isLimitedAccess: boolean,
  appointment?: Appointment,
}

export interface SoldToAddress {
  contact: string,
  company: string,
  phone: string,
  addr1: string,
  addr2?: string,
  addr3?: string,
  countryCode: string,
  postalCode: string,
  province?: string,
  city: string,
  emails?: Array<string>,
}

export type SoldToEntityMatch = 'same' | 'different';

export interface SoldToState {
  entityMatch: SoldToEntityMatch,
  address: SoldToAddress,
}

export interface PackageItem {
  packageType: keyof typeof PackageType,
  weight: number,
  length: number,
  width: number,
  height: number,
  dimUnits: keyof typeof DimensionUnitType | '',
  weightUnits: keyof typeof WeightUnitType | '',
  insurance: number,
  isAdditionalHandling: boolean,
  description?: string,
  signatureOptions?: keyof typeof SignatureType,
}

export interface TempPackageData {
  packageType: keyof typeof PackageType,
  weight: number | '',
  length: number | '',
  width: number | '',
  height: number | '',
  dimUnits: keyof typeof DimensionUnitType | '',
  weightUnits: keyof typeof WeightUnitType | '',
  insurance: number | '',
  isAdditionalHandling: boolean,
  description?: string,
  signatureOptions?: keyof typeof SignatureType | '',
}

export interface GetRatesData {
  fromAddress: {
    postalCode: string,
    addr1: string,
    city: string,
    countryCode: string,
  },
  toAddress: {
    postalCode: string,
    addr1: string,
    city: string,
    countryCode: string,
  },
  packages: Array<PackageItem>,
  shipDateTime: string,
  currencyCode: string,
  documentsOnly: boolean,
  returnShipment?: boolean,
  labelFree?: boolean,
  boxFree?: boolean,
}

export interface GeneralInfo {
  reasonForExport: keyof typeof ExportReasonType | '',
  generalDescriptionOfGoods?: string,
  currencyCode?: string,
  remarks?: string,
  statement?: string,
  declarationStatement?: string,
  taxId?: string,
  includeUsmca?: boolean,
}

export interface PartItem {
  quantity: number,
  detailedDescription: string,
  countryOfOrigin: string,
  hsCode: string,
  unitValue: number,
  quantityUnits: string,
  id?: number,
  nickname?: string,
  merchantProductId?: string,
  manufacturerProductId?: string,
  standardProductId?: string,
}

export interface TemporaryCustomsParts {
  nickname?: string;
  quantity?: string | number;
  quantityUnits?: string;
  detailedDescription?: string;
  countryOfOrigin?: string;
  hsCode?: string;
  unitValue?: string | number;
  merchantProductId?: string;
  manufacturerProductId?: string;
  standardProductId?: string;
}

export interface GeneralInfoInProgress extends GeneralInfo {
  customsParts: Array<PartItem>,
  temporaryCustomsParts?: TemporaryCustomsParts,
}

export interface PaymentInfoResponse {
  cardType?: keyof typeof CardType,
  partialCard?: string,
  printDisabled: boolean,
  verificationRequired: boolean,
  paymentMethod: keyof typeof PaymentMethodType,
  dateEntered?: string,
  maxAttemptsReached: boolean,
  attempts?: number,
  maxAttempts: number,
}

export interface PaymentInfo {
  cardNumber: string,
  month: string,
  year: string,
  cvv: string,
  address: {
    addr1: string,
    addr2?: string,
    addr3?: string,
    countryCode: string,
    postalCode: string,
    city?: string,
    province?: string,
  }
}

export interface PaymentInfoFormData extends PaymentInfo {
  contact: string,
  useShippingAddress: boolean,
}

export interface PaymentInfoRequest extends PaymentInfo {
  firstname: string,
  lastname: string,
  userAgent: string,
}

export interface PaymentSavedResponse {
  success: boolean,
  error: string,
  cardType: keyof typeof CardType,
  expiry: string | number
}

export interface AddressBookItem extends ShipFrom {
  id: number,
  nickname: string,
  addr3?: string,
  emails?: Array<string>,
  taxId?: string,
}

export interface deliveryTimeType {
  time: string,
  friendlyTime: string,
  isExact: boolean,
}

export interface deliveryTimeFormattedType {
  date: string,
  time: string,
  isTag: boolean,
}

export interface LandedCostCharge {
  name: string,
  amount: number,
}

export interface LandedCostResponse {
  currencyCode: string,
  importCountryCode: string,
  charges: LandedCostCharge[],
  totalDuty: number,
  totalTax: number,
  totalBrokerage: number,
  grandTotal: number,
}

export interface AvailableService {
  carrierCode: keyof typeof CarrierCodeType,
  selectedService: string,
  serviceName: string,
  typeOfRate: keyof typeof TypeOfRateType,
  currencyCode: string,
  total: number,
  subTotal: number,
  cutoffTime?: string,
  charges: Array<{ name: string, amount: number }>,
  show?: boolean,
  id?: number,
  warnings?: Array<string>,
  pickupAvailable: boolean,
  pickupFee: number,
  rateZone: string,
  regularPrice: number,
  tags?: Array<string>,
  trackingIncluded: boolean,
  useSecureship: boolean,
  billableWeight: {
    value: number,
    units: keyof typeof WeightUnitType,
  },
  taxDetails: {
    amount: number,
    rate: number,
    name: string,
  },
  fuelSurcharge: {
    amount: number,
    percentage: number,
  },
  deliveryTime: deliveryTimeType,
  usersCarrierAccountNo?: string,
  isAutoSwitchPermitted?: boolean,
  totals?: {
    published?: number,
    secureship?: number,
    myAccount?: number,
  },
  landedCost?: LandedCostResponse | null,
}

export interface ReconnectAccount {
  carrierCode: string,
  required?: boolean,
  reason?: string,
}

export type ReconnectAccounts = ReconnectAccount[];

export interface ShipmentEcommerceType {
  id: number,
  platformType: string,
  shopDetails: {
    name: string,
    url: string,
  },
  uniqueOrderId: string,
  orderId: string,
  fulfillOrder: boolean,
  storeNotification: boolean,
  connectionId?: number,
}

export interface InProgress {
  carrierCode: keyof typeof CarrierCodeType | string,
  serviceLevel: string,
  lastUpdated?: string,
  shipDate?: string,
  saturdayDelivery: boolean,
  documentsOnly: boolean,
  fromAddress: ShipFrom,
  toAddress: ShipTo,
  soldTo?: SoldToState,
  soldToAddress?: SoldToAddress,
  emailNotifications: Array<{
    email: {
      emailAddress: string,
      name?: string,
    },
    delivery: boolean,
    exception: boolean,
    ship: boolean
  }>,
  references: Array<string | null>,
  comments?: string | null,
  billing: {
    billingType: keyof typeof BillingType | '',
    account?: string,
  },
  temporaryPackage: TempPackageData,
  packages: Array<PackageItem>,
  ecommerce: ShipmentEcommerceType,
  commercialInvoice: GeneralInfoInProgress,
  signatureOptions: keyof typeof SignatureType | '',
  useSecureship?: boolean,
  editTrackingNumber?: string,
  miscellaneous?: {
    bookingNumber?: string,
    shippersLoadAndCount?: string,
  },
  isReturnShipment: boolean,
  isLabelFree: boolean,
  isBoxFree: boolean,
  uploadDocuments?: Array<UploadDocument>,
}

export interface StoreForImport {
  id: number,
  platformType: string,
  shopDetails: {
    name: string,
    url: string,
  },
  allowedOptions?: {
    fulfillOrder: boolean,
    storeNotification: boolean,
  },
  uniqueOrderId?: string,
}

export interface IntegrationsResponse extends GlobalResponseType {
  successful: boolean,
  platformType?: string,
  uniqueOrderId?: string,
  numberOfOrdersFound?: number,
  reinstall?: boolean,
  missingShippingAddress?: boolean,
  message?: string,
  fromAddress?: ShipFrom,
  toAddress?: ShipTo,
  temporaryPackage?: PackageItem,
  packages?: PackageItem[],
  commercialInvoice?: GeneralInfoInProgress,
}

export interface LabelMetaData {
  cardLookupId?: string | null,
  browserUserAgent?: string,
  browserScreenHeight?: number,
  browserScreenWidth?: number,
}

export interface SoldToAddressRequest {
  company?: string,
  contact?: string,
  phone?: string,
  addr1?: string,
  addr2?: string,
  addr3?: string,
  postalCode?: string,
  city?: string,
  province?: string,
  residential?: boolean,
  taxId?: string,
  emails?: string[],
}

export interface GetLabelData {
  selectedService: string,
  fromAddress: ShipFrom,
  toAddress: ShipTo,
  packages: Array<PackageItem>,
  shipDateTime: string,
  commercialInvoice?: GeneralInfoInProgress,
  deliveryEmails: Array<string>,
  billingOption?: string | null,
  documentsOnly?: boolean | false,
  billingAccountNumber?: string,
  ecommerce?: ShipmentEcommerceType,
  references: (string | null)[],
  comments: string;
  clearInProgress?: boolean,
  editTrackingNumber?: string,
  returnShipment?: boolean,
  labelFree?: boolean,
  boxFree?: boolean,
  metaData?: LabelMetaData,
  documentIds?: string[],
  soldToAddress?: SoldToAddressRequest,
}

export interface ThreeDsChallengeDto {
  threeDSecureChallengeUrl: string;
  threeDSecureChallengeData: string; // creq
  threeDSecureAuthenticationId: string;
  threeDSecureVersion: string;
}

export interface CarrierLabelResult extends GlobalResponseType {
  secureshipTrackingNo: string,
  carrierTrackingNos: Array<string>,
  errorType?: string,
  isExternalLink?: boolean,
  threeDsChallenge?: ThreeDsChallengeDto,
}

export interface ShipmentPrintData {
  successful: boolean,
  trackingNumber: string,
  carrierTrackingNumbers: string[],
  cutoffTime: string,
  isPickupAvailable: boolean,
  labelSize: keyof typeof PrintLabelSize,
  isReturnShipment?: boolean,
  isLabelFree?: boolean,
  isBoxFree?: boolean,
}

export interface Shipment {
  whereShipmentForm: VForm | null,
  whatShipmentForm: VForm | null,
  generalInfoForm: VForm | null,
  showNotification?: boolean,
  showSummaryPage?: boolean,
  selectedService: AvailableService | null,
  reconnectAccountRequired: ReconnectAccounts | null,
  addressBook: Array<AddressBookItem>,
  availableServices: Array<AvailableService> | null,
  inProgress: InProgress,
  isInProgressData: number,
  isInProgressDataChanged: boolean,
  storesForImport: StoreForImport[],
  secureshipTrackingNo: string,
}

export interface ShippingTips {
  hasResource: boolean,
  url: string,
  countryCode : string,
  iataCountryCode : string,
  personalEffects : {
    isRequired: boolean,
    name?: string,
    link?: string,
  }
}

export interface AmountVerificationResponse {
  amountConfirmed: boolean,
  errorType: string,
  errorMessage: string,
}

export interface AdvisorNotifications {
  isPoBox: boolean,
  isFragile: boolean,
  isPickupComments: boolean,
  isSignatureRequested: boolean,
  isCallRequested:boolean
}

export interface CarrierServiceLevel {
  carrierCode: string,
  selectedService: string,
  displayName: string
}

export interface HistoryTrackingResponse extends GlobalResponseType {
  allowed?: boolean,
  isInTransit?: boolean,
  isInProgressDetected?: boolean,
}

export interface CityProvinceResidentialResponse {
  city: string,
  province: string,
  postalCode: string,
  countryCode: string,
  isResidential: boolean
}

export interface ShipmentExtras {
  signature: keyof typeof SignatureType,
  reference: string,
  comments: string,
}

export interface OrderInfoDetails {
  title?: string;
  count: number;
  image?: string;
}

export interface OrderInfo {
  order?: string;
  date?: string;
  customer?: string;
  fulfillmentStatus?: string;
  count: number;
  details?: OrderInfoDetails[];
}

export type PredictionAddressDto = {
  description: string;
  placeId: string;
};

export type ClarificationAddressDto = {
  addr1?: string;
  countryCode?: string;
  postalCode?: string;
  city?: string;
  province?: string;
};

export interface ProvinceMetadata {
  'iso3166-2-code': string;
  displayName: string;
  capital?: string;
  googleMaps?: string;
}

export interface CountryMetadataResponse {
  alpha2: string;
  name: string;
  province?: {
    required: boolean;
    showField: boolean;
    items: ProvinceMetadata[];
  };
}

export interface ProvinceLookupDto {
  code?: string;
  displayName?: string;
}

export type PayDetailsAddressDto = {
  addr1?: string;
  countryCode?: string;
  postalCode?: string;
  city?: string;
  province?: ProvinceLookupDto;
};

export interface TemporaryPackage {
    packageType: keyof typeof PackageType;
    weight: number | '';
    length: number | '';
    width: number | '';
    height: number | '';
    dimUnit: keyof typeof DimensionUnitType | '';
    weightUnit: keyof typeof WeightUnitType | '';
    insurance: number | '';
    isAdditionalHandling: boolean;
    description?: string;
}

export type LandingPageEstimateData = {
    fromAddress: {
        countryCode: string;
        postalCode: string;
        addr1?: string;
        city?: string;
        province?: string;
        residential?: boolean;
        isInside?: boolean;
        isTailGate?: boolean;
        isTradeShow?: boolean;
        isLimitedAccess?: boolean;
    };
    toAddress: {
        countryCode: string;
        postalCode: string;
        addr1?: string;
        city?: string;
        province?: string;
        residential?: boolean;
        isInside?: boolean;
        isTailGate?: boolean;
        isTradeShow?: boolean;
        isLimitedAccess?: boolean;
    };
    packages: PackageItem[];
    temporaryPackage: TemporaryPackage;
    isDocumentsOnly?: boolean;
    signatureOption: keyof typeof SignatureType | '';
};

export interface ThreeDsCardLookupDto {
  cardLookupId: string | null;
  threeDSecureVersion: string | null;
  threeDSecureMethodUrl: string | null;
  threeDSecureMethodData: string | null;
}
